import { Box, Stack, Typography } from "@mui/material";
import {
  basicColours,
  Play20Px,
  ProgressButton,
  Stop20Px
} from "@qubit/autoparts";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import TestingToolLayout from "~/features/testingTools/TestingToolLayout";
import {
  addEvent,
  resetAllBinCircusPortStatuses,
  resetSingleStepSession,
  selectBinCircusPortStatusById,
  selectTaskGroupSize,
  setBinCircusPortCycling,
  setBinCircusPortError,
  setBinCircusPortIdle,
  setSingleStepTestStatus
} from "~/features/testingTools/testingTools.slice";
import { TestingTool } from "~/features/testingTools/testingTools.types";
import { runAsyncSafely } from "~/lib/helpers";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { useGridV2Subscription } from "~/lib/signalr";
import {
  selectThisWorkstation,
  selectWorkstationAvailablePorts,
  selectWorkstationIsMultiPortLayout,
  selectWorkstationParentPortId
} from "~/redux/selectors/workstationsSelectors";

import {
  useStartBinCircusMutation,
  useStopBinCircusMutation
} from "~/redux/warehouse/testingTools.hook";

import TaskGroupSizePicker from "./TaskGroupSizePicker";
import WorkstationPort from "./WorkstationPort";

type BinToPortTestProps = {
  onTestingSessionStart: (testType: TestingTool) => Promise<void>;
};

const BinToPortTest = ({ onTestingSessionStart }: BinToPortTestProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const workstation = useAppSelector(selectThisWorkstation);
  const workstationAvailablePorts = useAppSelector(
    selectWorkstationAvailablePorts
  );
  const ports = useAppSelector(selectWorkstationAvailablePorts);
  const columns = workstationAvailablePorts.length;
  const parentPortId = useAppSelector(selectWorkstationParentPortId);
  const isMultiPortLayout = useAppSelector(selectWorkstationIsMultiPortLayout);

  const portStatusesById = useAppSelector(selectBinCircusPortStatusById);
  const taskGroupSize = useAppSelector(selectTaskGroupSize);
  const isStopBinCircusBtnDisabled = ports.every(
    (portId) => portStatusesById[portId]?.portState !== "cycling"
  );
  const isStartBinCircusBtnDisabled = ports.every(
    (portId) => portStatusesById[portId]?.portState === "cycling"
  );

  // Track port statuses for cleanup - need ref since cleanup runs after state is wiped
  const portStatusesByIdRef = useRef(portStatusesById);
  useEffect(() => {
    portStatusesByIdRef.current = portStatusesById;
  }, [portStatusesById]);

  const [startBinCircus] = useStartBinCircusMutation();
  const [stopBinCircus] = useStopBinCircusMutation();

  useGridV2Subscription((event) => {
    if (
      event.case === "PortError" &&
      event.event.hasError &&
      event.event.portId &&
      ports.includes(event.event.portId)
    ) {
      dispatch(
        addEvent({
          text: `Log Publisher: Port error for port ${event.event.portId}`,
          state: "Failed",
          testOrigin: "Bin to Port"
        })
      );
    }
    if (
      event.case === "BinModeChange" &&
      event.event.binMode === "O" &&
      event.event.portId &&
      ports.includes(event.event.portId)
    ) {
      dispatch(
        addEvent({
          text: `Log Publisher: Bin ${event.event.binId} arrived at port ${event.event.portId}`,
          state: "Info",
          testOrigin: "Bin to Port"
        })
      );
    }
  });

  const startBinCircusSafelyByPort = useCallback(
    async (portId: number, shouldSkipStateUpdates?: boolean) => {
      if (!workstation) return;

      await runAsyncSafely(
        startBinCircus({
          portId,
          gridId: workstation.autostoreGridId,
          taskGroupSize
        }).unwrap(),
        {
          onSuccess: (response) => {
            // Skip state updates if requested (e.g., during cleanup scenarios)
            if (shouldSkipStateUpdates) return;

            dispatch(setBinCircusPortCycling(portId));
            dispatch(
              addEvent({
                text: response,
                state: "Success",
                testOrigin: "Bin to Port"
              })
            );
          },
          onError: (error) => {
            // Skip state updates if requested (e.g., during cleanup scenarios)
            if (shouldSkipStateUpdates) return;

            const errorEventMessage = getMessageFromRtkError(error);

            dispatch(
              setBinCircusPortError({
                portId,
                error: errorEventMessage,
                errorType: "start"
              })
            );
            dispatch(
              addEvent({
                text: errorEventMessage,
                state: "Failed",
                testOrigin: "Bin to Port"
              })
            );
            dispatch(
              setSingleStepTestStatus({
                testingToolName: "Bin to Port",
                eventStatus: "Failed",
                skipLogging: true
              })
            );
          }
        }
      );
    },
    [dispatch, startBinCircus, taskGroupSize, workstation]
  );

  const stopBinCircusSafelyByPort = useCallback(
    async (portId: number, shouldSkipEventLogging?: boolean) => {
      if (!workstation) return;

      await runAsyncSafely(
        stopBinCircus({
          portId,
          gridId: workstation.autostoreGridId
        }).unwrap(),
        {
          onSuccess: (response) => {
            // Skip state updates during unmount
            if (shouldSkipEventLogging) return;

            dispatch(setBinCircusPortIdle(portId));
            dispatch(
              addEvent({
                text: response,
                state: "Success",
                testOrigin: "Bin to Port"
              })
            );
          },
          onError: (error) => {
            // Skip state updates during unmount
            if (shouldSkipEventLogging) return;

            const errorEventMessage = getMessageFromRtkError(error);

            dispatch(
              setBinCircusPortError({
                portId,
                error: errorEventMessage,
                errorType: "stop"
              })
            );
            dispatch(
              addEvent({
                text: errorEventMessage,
                state: "Failed",
                testOrigin: "Bin to Port"
              })
            );
            dispatch(
              setSingleStepTestStatus({
                testingToolName: "Bin to Port",
                eventStatus: "Failed",
                skipLogging: true
              })
            );
          }
        }
      );
    },
    [dispatch, stopBinCircus, workstation]
  );

  const startBinCircusTest = useCallback(async () => {
    if (!workstation || !ports?.length) return;

    // Reset all port statuses before starting
    dispatch(resetAllBinCircusPortStatuses());

    // Set session status to "running" (skip logging - individual port starts are logged)
    dispatch(
      setSingleStepTestStatus({
        testingToolName: "Bin to Port",
        eventStatus: "Info",
        skipLogging: true
      })
    );

    await Promise.all(
      ports.map((portId) => startBinCircusSafelyByPort(portId))
    );
  }, [dispatch, ports, startBinCircusSafelyByPort, workstation]);

  const stopBinCircusTest = useCallback(async () => {
    if (!workstation || !ports.length) return;

    const cyclingPorts = ports.filter(
      (portId) =>
        portStatusesById[portId] !== undefined &&
        portStatusesById[portId]?.portState === "cycling"
    );

    await Promise.all(
      cyclingPorts.map((portId) => stopBinCircusSafelyByPort(portId))
    );
  }, [ports, portStatusesById, stopBinCircusSafelyByPort, workstation]);

  // Stop cycling ports on component unmount (navigation)
  useEffect(() => {
    return () => {
      if (workstation && ports.length) {
        const cyclingPorts = ports.filter(
          (portId) =>
            portStatusesByIdRef.current[portId]?.portState === "cycling"
        );
        cyclingPorts.forEach((portId) => {
          void stopBinCircusSafelyByPort(
            portId,
            true /* shouldSkipEventLogging */
          );
        });
      }

      // Reset session state after initiating stop calls
      // The shouldSkipEventLogging flag ensures async callbacks won't re-populate state
      dispatch(resetSingleStepSession("Bin to Port"));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if all ports completed successfully (stopped with completedOn timestamp)
  useEffect(() => {
    if (!ports.length || Object.keys(portStatusesById).length === 0) return;

    const allPortsCompleted = ports.every(
      (portId) =>
        portStatusesById[portId] !== undefined &&
        portStatusesById[portId]?.completedOn !== null
    );

    if (allPortsCompleted) {
      // Set session status to "completed" (skip logging - individual port stops are logged)
      dispatch(
        setSingleStepTestStatus({
          testingToolName: "Bin to Port",
          eventStatus: "Success",
          skipLogging: true
        })
      );
    }
  }, [dispatch, ports, portStatusesById]);

  return (
    <TestingToolLayout>
      <Stack
        sx={{
          gap: 4,
          alignItems: "center",
          justifyContent: "center",
          height: "100%"
        }}
        aria-label="Bin to port test content"
        role="region"
      >
        <TaskGroupSizePicker />
        <Box
          sx={{
            display: "grid",
            gridAutoFlow: "row",
            gridTemplateColumns: isMultiPortLayout
              ? "minmax(0, 1fr)"
              : `repeat(${columns}, minmax(250px, 350px))`,
            justifyItems: "stretch",
            justifyContent: "center",
            width: "100%",
            gap: 4,
            "@media (max-width: 900px)": {
              gridTemplateColumns: "1fr",
              gap: 2,
              gridAutoFlow: "row"
            }
          }}
        >
          {parentPortId !== null && (
            <Typography
              variant="h4"
              fontWeight={500}
              textAlign="center"
              gridColumn={isMultiPortLayout ? "1 / -1" : `span ${columns}`}
            >
              {`${t("Parent Port")} ${parentPortId}`}
            </Typography>
          )}
          {ports.map((portId) => (
            <WorkstationPort
              key={portId}
              portId={portId}
              onRetryStartBinCircus={startBinCircusSafelyByPort}
              onRetryStopBinCircus={stopBinCircusSafelyByPort}
            />
          ))}
        </Box>
        <Stack flexDirection="row" alignItems="center" gap={2}>
          <ProgressButton
            onClick={async () => {
              await onTestingSessionStart("Bin to Port");
              await startBinCircusTest();
            }}
            disabled={isStartBinCircusBtnDisabled}
            variant="contained"
            color="primary"
            sx={{
              minWidth: 130,
              height: 55,
              fontSize: "1.25rem"
            }}
            startIcon={
              <Play20Px
                fill={basicColours.mono.white}
                width={30}
                height={30}
                style={{
                  opacity: isStartBinCircusBtnDisabled ? 0.3 : 1
                }}
              />
            }
          >
            {t("start")}
          </ProgressButton>
          <ProgressButton
            onClick={stopBinCircusTest}
            disabled={isStopBinCircusBtnDisabled}
            variant="contained"
            color="secondary"
            startIcon={
              <Stop20Px
                width={30}
                height={30}
                style={{
                  opacity: isStopBinCircusBtnDisabled ? 0.3 : 1
                }}
              />
            }
            sx={{
              minWidth: 130,
              height: 55,
              fontSize: "1.25rem"
            }}
          >
            {t("stop")}
          </ProgressButton>
        </Stack>
      </Stack>
    </TestingToolLayout>
  );
};

export default BinToPortTest;
